/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListChatSessionLinksRequest,
  ListChatSessionLinksSecurity,
} from "../models/operations/listchatsessionlinks.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListChatSessionLinksQuery,
  ListChatSessionLinksQueryData,
  prefetchListChatSessionLinks,
  queryKeyListChatSessionLinks,
} from "./listChatSessionLinks.core.js";
export {
  buildListChatSessionLinksQuery,
  type ListChatSessionLinksQueryData,
  prefetchListChatSessionLinks,
  queryKeyListChatSessionLinks,
};

export type ListChatSessionLinksQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSessionLinks chat
 *
 * @remarks
 * List session-lineage links touching the given chats (session portability). A link records that a session was moved to another harness; the child side is present when the continuation's session id was known at move time. Returns every link where a requested chat is either the parent or the child, honoring the same visibility scoping as listChats.
 */
export function useListChatSessionLinks(
  request: ListChatSessionLinksRequest,
  security?: ListChatSessionLinksSecurity | undefined,
  options?: QueryHookOptions<
    ListChatSessionLinksQueryData,
    ListChatSessionLinksQueryError
  >,
): UseQueryResult<
  ListChatSessionLinksQueryData,
  ListChatSessionLinksQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListChatSessionLinksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSessionLinks chat
 *
 * @remarks
 * List session-lineage links touching the given chats (session portability). A link records that a session was moved to another harness; the child side is present when the continuation's session id was known at move time. Returns every link where a requested chat is either the parent or the child, honoring the same visibility scoping as listChats.
 */
export function useListChatSessionLinksSuspense(
  request: ListChatSessionLinksRequest,
  security?: ListChatSessionLinksSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListChatSessionLinksQueryData,
    ListChatSessionLinksQueryError
  >,
): UseSuspenseQueryResult<
  ListChatSessionLinksQueryData,
  ListChatSessionLinksQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListChatSessionLinksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListChatSessionLinksData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      chatIds: Array<string>;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramChatSession?: string | undefined;
    },
  ],
  data: ListChatSessionLinksQueryData,
): ListChatSessionLinksQueryData | undefined {
  const key = queryKeyListChatSessionLinks(...queryKeyBase);

  return client.setQueryData<ListChatSessionLinksQueryData>(key, data);
}

export function invalidateListChatSessionLinks(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      chatIds: Array<string>;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramChatSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "listSessionLinks", ...queryKeyBase],
  });
}

export function invalidateAllListChatSessionLinks(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "listSessionLinks"],
  });
}
